import {
    FreeOfChargeProductTemplateConfig,
    ProductTemplateConfigType,
} from './ProductTemplate.ts';

export const enum FixedDurationType {
    MONTH = 'MONTH',
    YEAR = 'YEAR',
}

export interface FixedDuration {
    type: FixedDurationType;
    quantity: number;
    price: number;
}

export enum FixedDurationName {
    ONE_MONTH = 'ONE_MONTH',
    TWO_MONTHS = 'TWO_MONTHS',
    THREE_MONTHS = 'THREE_MONTHS',
    FOUR_MONTHS = 'FOUR_MONTHS',
    FIVE_MONTHS = 'FIVE_MONTHS',
    SIX_MONTHS = 'SIX_MONTHS',
    SEVEN_MONTHS = 'SEVEN_MONTHS',
    EIGHT_MONTHS = 'EIGHT_MONTHS',
    NINE_MONTHS = 'NINE_MONTHS',
    TEN_MONTHS = 'TEN_MONTHS',
    ELEVEN_MONTHS = 'ELEVEN_MONTHS',
    ONE_YEAR = 'ONE_YEAR',
}

export function mapFixedDurationNameToValue(
    fixedDurationName: FixedDurationName,
) {
    switch (fixedDurationName) {
        case FixedDurationName.ONE_YEAR:
            return {
                type: FixedDurationType.YEAR,
                quantity: 1,
                price: 0,
            };
        case FixedDurationName.ELEVEN_MONTHS:
            return {
                type: FixedDurationType.MONTH,
                quantity: 11,
                price: 0,
            };
        case FixedDurationName.TEN_MONTHS:
            return {
                type: FixedDurationType.MONTH,
                quantity: 10,
                price: 0,
            };
        case FixedDurationName.NINE_MONTHS:
            return {
                type: FixedDurationType.MONTH,
                quantity: 9,
                price: 0,
            };
        case FixedDurationName.EIGHT_MONTHS:
            return {
                type: FixedDurationType.MONTH,
                quantity: 8,
                price: 0,
            };
        case FixedDurationName.SEVEN_MONTHS:
            return {
                type: FixedDurationType.MONTH,
                quantity: 7,
                price: 0,
            };
        case FixedDurationName.SIX_MONTHS:
            return {
                type: FixedDurationType.MONTH,
                quantity: 6,
                price: 0,
            };
        case FixedDurationName.FIVE_MONTHS:
            return {
                type: FixedDurationType.MONTH,
                quantity: 5,
                price: 0,
            };
        case FixedDurationName.FOUR_MONTHS:
            return {
                type: FixedDurationType.MONTH,
                quantity: 4,
                price: 0,
            };
        case FixedDurationName.THREE_MONTHS:
            return {
                type: FixedDurationType.MONTH,
                quantity: 3,
                price: 0,
            };
        case FixedDurationName.TWO_MONTHS:
            return {
                type: FixedDurationType.MONTH,
                quantity: 2,
                price: 0,
            };
        case FixedDurationName.ONE_MONTH:
            return {
                type: FixedDurationType.MONTH,
                quantity: 1,
                price: 0,
            };
    }
}

export function getDefaultDurationIfApplicable(
    freeOfChargeProductConfig: FreeOfChargeProductTemplateConfig,
): FixedDurationName | null {
    switch (freeOfChargeProductConfig.configType) {
        case ProductTemplateConfigType.ALL_ACCESS:
        case ProductTemplateConfigType.PERMIT_UNTIL_REVOCATION:
        case ProductTemplateConfigType.PERMIT_FREE_VALID_TO:
            return null;
        case ProductTemplateConfigType.PERMIT_FREE_FIXED_DURATION:
            return mapFixedDurationValueToName(
                freeOfChargeProductConfig.allowedDurations[0],
            );
    }
}

export function mapFixedDurationValueToName(
    duration: FixedDuration,
): FixedDurationName | null {
    if (duration.type === FixedDurationType.YEAR && duration.quantity === 1) {
        return FixedDurationName.ONE_YEAR;
    }

    if (duration.type === FixedDurationType.MONTH) {
        switch (duration.quantity) {
            case 11:
                return FixedDurationName.ELEVEN_MONTHS;
            case 10:
                return FixedDurationName.TEN_MONTHS;
            case 9:
                return FixedDurationName.NINE_MONTHS;
            case 8:
                return FixedDurationName.EIGHT_MONTHS;
            case 7:
                return FixedDurationName.SEVEN_MONTHS;
            case 6:
                return FixedDurationName.SIX_MONTHS;
            case 5:
                return FixedDurationName.FIVE_MONTHS;
            case 4:
                return FixedDurationName.FOUR_MONTHS;
            case 3:
                return FixedDurationName.THREE_MONTHS;
            case 2:
                return FixedDurationName.TWO_MONTHS;
            case 1:
                return FixedDurationName.ONE_MONTH;
        }
    }
    return null;
}

export function fixedDurationTextByName(durationName: FixedDurationName) {
    switch (durationName) {
        case FixedDurationName.ONE_YEAR:
            return {
                de: '1 Jahr',
                fr: '1 an',
                it: '1 anno',
                en: '1 year',
            };
        case FixedDurationName.ELEVEN_MONTHS:
            return {
                de: '11 Monate',
                fr: '11 mois',
                it: '11 mesi',
                en: '11 months',
            };
        case FixedDurationName.TEN_MONTHS:
            return {
                de: '10 Monate',
                fr: '10 mois',
                it: '10 mesi',
                en: '10 months',
            };
        case FixedDurationName.NINE_MONTHS:
            return {
                de: '9 Monate',
                fr: '9 mois',
                it: '9 mesi',
                en: '9 months',
            };
        case FixedDurationName.EIGHT_MONTHS:
            return {
                de: '8 Monate',
                fr: '8 mois',
                it: '8 mesi',
                en: '8 months',
            };
        case FixedDurationName.SEVEN_MONTHS:
            return {
                de: '7 Monate',
                fr: '7 mois',
                it: '7 mesi',
                en: '7 months',
            };
        case FixedDurationName.SIX_MONTHS:
            return {
                de: '6 Monate',
                fr: '6 mois',
                it: '6 mesi',
                en: '6 months',
            };
        case FixedDurationName.FIVE_MONTHS:
            return {
                de: '5 Monate',
                fr: '5 mois',
                it: '5 mesi',
                en: '5 months',
            };
        case FixedDurationName.FOUR_MONTHS:
            return {
                de: '4 Monate',
                fr: '4 mois',
                it: '4 mesi',
                en: '4 months',
            };
        case FixedDurationName.THREE_MONTHS:
            return {
                de: '3 Monate',
                fr: '3 mois',
                it: '3 mesi',
                en: '3 months',
            };
        case FixedDurationName.TWO_MONTHS:
            return {
                de: '2 Monate',
                fr: '2 mois',
                it: '2 mesi',
                en: '2 months',
            };
        case FixedDurationName.ONE_MONTH:
            return {
                de: '1 Monat',
                fr: '1 mois',
                it: '1 mese',
                en: '1 month',
            };
    }
}
